import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';


const YouTubeVideo = ({ videoId, autoplay = false, controls = true, mute = false }: { videoId: string, autoplay?: boolean, controls?: boolean, mute?: boolean }) => {
  const src = `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&controls=${controls ? 1 : 0}&mute=${mute ? 1 : 0}`;

  return (
    <div className="aspect-video rounded-lg overflow-hidden shadow-lg h-full">
      <iframe
        title="Youtube"
        src={src}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      ></iframe>
    </div>
  );
};


const DoctorMessage: React.FC = () => {

  const videoRef = useRef(null);
  const [isVideoVisible, setIsVideoVisible] = React.useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVideoVisible(true);
        } else {
          setIsVideoVisible(false);
        }
      });
    }, options);

    const currentRef = videoRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);



  return (
    <section className="py-16 md:py-24 px-4 bg-softwhite relative overflow-hidden">
      {/* Decorative elements */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.1 }}
        transition={{ duration: 1 }}
        className="absolute top-0 right-0 w-96 h-96 bg-golden-yellow rounded-full blur-3xl"
      />

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/4] relative">
              <img
                src="/images/doc.jpeg"
                alt="Dr. Hirak Pahari"
                className="w-full h-full object-cover rounded-lg shadow-xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-darkbrown/20 to-transparent rounded-lg" />
            </div>

            {/* Experience Badge */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="absolute -right-6 top-8 bg-golden-yellow text-darkbrown p-4 rounded-full shadow-lg"
            >
              <span className="text-2xl font-bold">15+</span>
              <span className="block text-sm">Years Exp.</span>
            </motion.div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-darkbrown">
              A Message from Dr. Hirak Pahari
            </h2>

            <div className="w-20 h-1 bg-golden-yellow" />

            <p className="text-lg text-charcol leading-relaxed">
              "With over 15 years of dedicated service in hepatology and liver care,
              I understand the profound impact liver health has on overall wellbeing.
              My commitment goes beyond treatment – it's about partnering with you
              on your journey to better health."
            </p>

            <div className="space-y-4 text-earthybeige">
              <p className="leading-relaxed">
                My approach combines cutting-edge medical expertise with compassionate
                care. Every patient receives personalized attention and access to the
                latest advancements in liver treatment.
              </p>

              <p className="leading-relaxed">
                Together, we'll work towards not just managing liver conditions, but
                achieving optimal health outcomes that enhance your quality of life.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="pt-6"
            >
              <p className="text-brown font-medium mt-2">Dr. Hirak Pahari</p>
              <p className="text-sm text-earthybeige">Leading Hepatologist & Liver Transplant Surgeon</p>
            </motion.div>
          </motion.div>
        </div>

        {/* Videos Section - Centered and responsive */}
        <div className="mt-16 flex justify-center">
          <div
            ref={videoRef}
            className="w-full lg:w-3/4 grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <div className="rounded-xl overflow-hidden shadow-2xl aspect-video">
              <YouTubeVideo
                videoId="qlNmkEXhP1Y"
                autoplay={isVideoVisible}
                controls={false}
                mute={false}
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-2xl aspect-video">
              <YouTubeVideo
                videoId="CAzhVsumNJI"
                autoplay={isVideoVisible}
                controls={false}
                mute={false}
              />
            </div>
          </div>
        </div>

        {/* view all services button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center mt-16"
        >
          <Link to="/services">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-charcol hover:bg-palegold text-white px-8 py-3 rounded-lg font-semibold flex items-center transition-colors"
            >
              Learn More
              <ChevronRight className="w-5 h-5 ml-2" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default DoctorMessage;